import { api } from './apiClient';
import type { Artifact } from '../types';

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const safeName = (name: string) => (name || 'artefato').replace(/[^\w\-. ]+/g, '_').slice(0, 80);

// Monta o HTML "renderizável" do artefato (usado no preview e no PDF client-side).
export function artifactToHtml(a: Artifact): string {
  if (a.type === 'html') return a.content;
  // outros tipos: deixa o preview do componente cuidar; aqui é fallback simples.
  return `<pre>${a.content.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string))}</pre>`;
}

export const artifactsService = {
  async listByAgent(agentId: string): Promise<Artifact[]> {
    const res = await api.get<{ artifacts: Artifact[] }>(`/artifacts?agentId=${encodeURIComponent(agentId)}`);
    return res.artifacts;
  },
  async get(id: string): Promise<Artifact> {
    const res = await api.get<{ artifact: Artifact }>(`/artifacts/${encodeURIComponent(id)}`);
    return res.artifact;
  },
  async remove(id: string): Promise<void> {
    await api.del(`/artifacts/${encodeURIComponent(id)}`);
  },

  // --- Export ---
  // Servidor: PDF / DOCX / XLSX (conversão de alta fidelidade).
  async exportServer(a: Artifact, format: 'pdf' | 'docx' | 'xlsx'): Promise<void> {
    const blob = await api.download(`/artifacts/${encodeURIComponent(a.id)}/export?format=${format}`);
    saveBlob(blob, `${safeName(a.name)}.${format}`);
  },
  // Cliente: HTML, CSV, JSON, código (texto puro).
  exportText(a: Artifact, ext: string, mime: string): void {
    saveBlob(new Blob([a.content], { type: mime }), `${safeName(a.name)}.${ext}`);
  },
  // Cliente: PDF a partir de um elemento renderizado (html2pdf), com paginação
  // adequada (multipágina). O chamador garante um elemento SEM altura limitada.
  async exportPdfFromElement(el: HTMLElement, name: string): Promise<void> {
    const html2pdf = (await import('html2pdf.js')).default;
    const opts = {
      margin: [10, 10, 12, 10],
      filename: `${safeName(name)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy', 'avoid-all'] },
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await html2pdf().set(opts as any).from(el).save();
  },
};
